import React, { useEffect, useRef } from 'react';
import './NewHeroHeader.css';

const LOGOS = [
    { name: 'Dell', label: 'Dell Certified' },
    { name: 'HP', label: 'HP Authorized' },
    { name: 'Lenovo', label: 'Lenovo Partner' },
    { name: 'Apple', label: 'Apple Products' },
    { name: 'Asus', label: 'Asus Certified' },
];

export default function NewHeroSection() {
    const heroRef = useRef(null);
    const canvasRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => {
            if (!heroRef?.current) return;
            const scrollY = window.scrollY;
            const parallaxEls = heroRef?.current?.querySelectorAll('[data-parallax]');
            parallaxEls?.forEach((el) => {
                const speed = parseFloat(el?.dataset?.parallax || '0.3');
                el.style.transform = `translateY(${scrollY * speed}px)`;
            });
        };
        window.addEventListener('scroll', handleScroll, { passive: true });

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('animate');
                }
            });
        }, { threshold: 0.1 });

        const animateEls = heroRef.current?.querySelectorAll('.animate-on-scroll');
        if (animateEls) {
            animateEls.forEach(el => observer.observe(el));
        }

        return () => {
            window.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        let animationFrameId;

        const initZdogAnimation = () => {
            const Zdog = window.Zdog;
            if (!Zdog || !canvasRef.current) return;

            const { Anchor, Box, TAU } = Zdog;
            const sineOut = (t) => Math.sin((t * Math.PI) / 2);

            const element = canvasRef.current;
            const context = element.getContext("2d");
            const { width, height } = element;
            const zoom = 8;

            const columns = 7;
            const rows = 7;

            const jumps = [1, 1, 1, -1, -1, -1, 2, -2];
            const lift = 3;
            const size = 5;
            const gap = 6;
            const innerSize = size / 2.5;

            const root = new Anchor();

            const colors = {
                boxes: {
                    color: "hsl(209 34% 30%)",
                    leftFace: "hsl(209 61% 16%)",
                    frontFace: "hsl(211 39% 23%)"
                },
                box: {
                    color: "hsl(202 83% 41%)",
                    x: "hsl(199 84% 55%)",
                    y: "hsl(196 94% 67%)"
                }
            };

            const { color, leftFace, frontFace } = colors.boxes;

            const boxes = new Anchor({
                addTo: root
            });

            for (let i = 0; i < rows; i++) {
                for (let j = 0; j < columns; j++) {
                    const x = j * gap;
                    const z = i * gap * -1;

                    new Box({
                        addTo: boxes,
                        color,
                        leftFace,
                        frontFace,
                        stroke: 0,
                        width: size,
                        height: size,
                        depth: size,
                        translate: { x, z }
                    });
                }
            }

            boxes.translate.y = size / 2 + innerSize / 2;

            const box = new Box({
                addTo: root,
                color: colors.box.color,
                rightFace: colors.box.x,
                leftFace: colors.box.x,
                topFace: colors.box.y,
                bottomFace: colors.box.y,
                stroke: 0,
                width: innerSize,
                height: innerSize,
                depth: innerSize
            });

            context.lineCap = "round";
            context.lineJoin = "round";

            const render = () => {
                context.clearRect(0, 0, width, height);
                context.save();
                context.translate(width / 2, height / 2);
                context.scale(zoom, zoom);
                root.renderGraphCanvas(context);
                context.restore();
            };

            let column = Math.floor(Math.random() * columns);
            let row = Math.floor(Math.random() * rows);
            let c = 0;
            let r = 0;

            if (Math.random() > 0.5) {
                c = column > columns / 2 ? -1 : 1;
            } else {
                r = row > rows / 2 ? -1 : 1;
            }

            box.translate.x = column * gap;
            box.translate.z = row * gap * -1;

            boxes.children[column + row * columns].color = box.bottomFace;

            root.rotate.x = (TAU / 12) * -1;
            root.rotate.y = (TAU / 8) * -1;
            root.translate.y = (Math.max(columns, rows) * gap) / 2 / 2;

            root.updateGraph();
            render();

            let ticker = 0;
            const cycle = 50;

            const animate = () => {
                ticker++;
                if (ticker >= cycle) {
                    ticker = ticker % cycle;

                    column += c;
                    row += r;

                    boxes.children[column + row * columns].color = box.bottomFace;

                    if (Math.random() > 0.5) {
                        const possibleJumps = jumps.filter((d) => {
                            const checkC = d + column;
                            return checkC >= 0 && checkC < columns;
                        });

                        c = possibleJumps[Math.floor(Math.random() * possibleJumps.length)];
                        r = 0;
                    } else {
                        const possibleJumps = jumps.filter((d) => {
                            const checkR = d + row;
                            return checkR >= 0 && checkR < rows;
                        });

                        c = 0;
                        r = possibleJumps[Math.floor(Math.random() * possibleJumps.length)];
                    }
                }

                const progress = ticker / cycle;
                const loop = Math.sin(progress * Math.PI);
                const ease = sineOut(progress);

                box.translate.x = column * gap + progress * c * gap;
                box.translate.z = row * gap * -1 + progress * r * gap * -1;
                box.translate.y = loop * lift * -1;
                box.rotate.x = ease * TAU * r;
                box.rotate.z = ease * TAU * c;

                root.updateGraph();
                render();

                animationFrameId = requestAnimationFrame(animate);
            };

            animationFrameId = requestAnimationFrame(animate);
        };

        if (!window.Zdog) {
            const script = document.createElement("script");
            script.src = "https://unpkg.com/zdog@1/dist/zdog.dist.min.js";
            script.onload = () => {
                initZdogAnimation();
            };
            document.body.appendChild(script);
        } else {
            initZdogAnimation();
        }

        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
        };
    }, []);

    const scrollToScheduler = () => {
        document.getElementById('demo-scheduler')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section
            ref={heroRef}
            className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-evergreen"
            id="homehero"
        >
            {/* Grid dot background */}
            <div
                className="absolute inset-0 grid-dot opacity-40"
                data-parallax="0.15"
                style={{ willChange: 'transform' }}
            />
            {/* Atmospheric blur orbs */}
            <div
                className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl"
                style={{ background: 'radial-gradient(circle, #2B7DE9, transparent)' }}
                data-parallax="0.08"
            />
            <div
                className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full opacity-8 blur-3xl"
                style={{ background: 'radial-gradient(circle, #004A8F, transparent)' }}
                data-parallax="0.12"
            />

            {/* Zdog Canvas Model placed organically behind the text as a hero element */}
            <div className="absolute inset-0 z-0 flex items-center justify-center opacity-70 pointer-events-none mt-20" data-parallax="0.05">
                <canvas
                    ref={canvasRef}
                    style={{ display: "block", inlineSize: "100%", maxInlineSize: "700px" }}
                    width="700"
                    height="500"
                ></canvas>
            </div>

            {/* Scan line overlay on hero */}
            <div className="scan-line absolute left-0 w-full h-24 z-10" />

            {/* Content */}
            <div className="relative z-20 text-center px-6 max-w-5xl mx-auto pt-28">
                {/* Status badge */}
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#0063B8]/30 bg-[#0063B8]/10 mb-10 animate-on-scroll anim-in">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0063B8] dot-blink" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#0063B8]">
                        10,000+ Happy Customers 
                    </span>
                </div>

                {/* Giant headline */}
                <h1 className="text-hero text-clinical-white mb-6 animate-on-scroll anim-in delay-100" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif" }}>
                    Premium Renewed.<br />
                    <span className="italic text-[#0063B8]">Unbeatable Prices.</span><br />
                    Trusted Quality.
                </h1>

                {/* Subline */}
                <p className="text-[15px] md:text-lg font-medium text-white uppercase tracking-[0.2em] mb-12 max-w-xl mx-auto animate-on-scroll anim-in delay-200" style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif" }}>
                    Certified refurbished laptops & desktops with 6-month warranty.
                </p>

                {/* CTA */}
                <div className="animate-on-scroll anim-in delay-300">
                    <button
                        onClick={scrollToScheduler}
                        className="btn-pulse inline-flex items-center gap-3 bg-teal hover:bg-teal-light text-white px-10 py-4 text-[12px] font-bold uppercase tracking-[0.3em] transition-colors duration-300 rounded-sm mb-14"
                    >
                        Explore Now
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>

                {/* Institutional logos */}
                <div className="animate-on-scroll anim-in delay-400">
                    <p className="text-[9px] uppercase tracking-[0.5em] text-white mb-6 font-semibold">
                        Brands We Carry
                    </p>
                    <div className="flex items-center justify-center gap-8 flex-wrap">
                        {LOGOS?.map((logo) => (
                            <div
                                key={logo?.name}
                                className="flex flex-col items-center gap-1 transition-opacity duration-300"
                                title={logo?.label}
                            >
                                <div className="w-16 h-8 border border-white/40 rounded flex items-center justify-center bg-white/10">
                                    <span className="text-[8px] font-black uppercase tracking-wider text-white">
                                        {logo?.name}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            {/* Scroll indicator */}
            <div className="absolute bottom-5 left-[92%] -translate-x-1/2 z-20 animate-on-scroll anim-in delay-600">
                <div className="w-px h-20 bg-gradient-to-b from-sage/50 to-transparent mx-auto" />
                <span className="text-[9px] uppercase tracking-[0.4em] text-white/30 font-semibold block mt-3 text-center">
                    Scroll
               </span>
            </div>
        </section>
    );
}
